import { useEffect, useState, useCallback, useMemo } from "react";
import {
  MagnifyingGlass,
  Trash,
  Repeat,
  ArrowClockwise,
  X,
  CaretDown,
  Tag,
  ArrowsHorizontal,
  Check,
  CheckCircle,
  Warning,
  CheckSquare,
  Square,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtMoney } from "../../lib/finance-format";
import { useCategories, broadcastCategoriesChanged } from "../../lib/useCategories";
import CategoryManagerDialog, { CategoryIcon } from "./CategoryManagerDialog";
import ReconciliationDialog from "./ReconciliationDialog";
import SimilarSuggestionDialog from "./SimilarSuggestionDialog";
import DateRangeChips from "./DateRangeChips";
import ColumnFilter, { ColumnFilterTrigger } from "./ColumnFilter";

const STATUS_OPTS = [
  { key: "all", label: "Todas" },
  { key: "pending", label: "Pendientes" },
  { key: "approved", label: "Aprobadas" },
];

/**
 * QuickBooks-style transaction ledger with:
 *   - editable categories
 *   - pending/approved approval workflow (yellow → green)
 *   - date range filter (12m + years)
 *   - inline reconciliation button
 */
export default function TransactionsPanel({ scope, onDataChanged, initialCategory, onInitialCategoryConsumed }) {
  const { categories, byKey, refresh: refreshCategories } = useCategories();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [deductibleOnly, setDeductibleOnly] = useState(false);
  const [range, setRange] = useState({ preset: "all", from: null, to: null });
  const [detecting, setDetecting] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  // Similar-transactions auto-suggest, triggered after a single-row category change
  const [similarOpen, setSimilarOpen] = useState(false);
  const [similarContext, setSimilarContext] = useState({ txId: null, category: null });
  // Excel-style per-column filters + sort
  const [colFilters, setColFilters] = useState({
    date:        { selected: new Set(), sortDir: null },
    description: { selected: new Set(), sortDir: null },
    category:    { selected: new Set(), sortDir: null },
    amount:      { selected: new Set(), sortDir: null },  // amount uses sortDir only
  });
  const [openFilter, setOpenFilter] = useState(null); // 'date' | 'description' | 'category' | 'amount' | null

  // Derived list — apply per-column filters + sort on top of `items`
  const visibleItems = useMemo(() => {
    let arr = [...items];
    // Filters
    for (const col of ["date", "description", "category"]) {
      const f = colFilters[col];
      if (f.selected.size === 0) continue;
      if (f.selected.has("__NONE__")) { arr = []; break; }
      const wanted = f.selected;
      arr = arr.filter((tx) => wanted.has(String(tx[col] ?? "")));
    }
    // Sort — first column with a sortDir wins (fine for a table view)
    const sortCol = Object.entries(colFilters).find(([, f]) => f.sortDir);
    if (sortCol) {
      const [col, { sortDir }] = sortCol;
      const dir = sortDir === "asc" ? 1 : -1;
      arr.sort((a, b) => {
        const av = a[col] ?? "";
        const bv = b[col] ?? "";
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return String(av).localeCompare(String(bv), "es", { numeric: true }) * dir;
      });
    }
    return arr;
  }, [items, colFilters]);

  // Unique values per column, for the filter popover checklists
  const uniqueValuesFor = (col) => {
    const map = new Map();
    for (const tx of items) {
      const raw = tx[col];
      const key = String(raw ?? "");
      if (!map.has(key)) map.set(key, { value: key, label: labelForCol(col, raw, tx), count: 0 });
      map.get(key).count += 1;
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "es"));
  };
  const labelForCol = (col, raw, tx) => {
    if (col === "category") return byKey[raw]?.label || String(raw ?? "sin");
    if (col === "amount") return `$${Number(raw || 0).toFixed(2)}`;
    return String(raw ?? "");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("scope", scope || "all");
      params.set("limit", "200");
      if (q.trim()) params.set("q", q.trim());
      if (category !== "all") params.set("category", category);
      if (status !== "all") params.set("status", status);
      if (deductibleOnly) params.set("is_deductible", "true");
      if (range.from) params.set("from", range.from);
      if (range.to) params.set("to", range.to);
      const { data } = await api.get(`/finance/transactions?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPendingCount(data.pending_count || 0);
    } catch (err) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [scope, q, category, status, deductibleOnly, range, reloadFlag]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // If the parent handed us an initial category (Reports tile click), apply it
  // to the category filter once, then clear the request so it doesn't re-trigger.
  useEffect(() => {
    if (!initialCategory) return;
    const meta = byKey[initialCategory];
    // Wait until categories are loaded so we can show the Spanish label
    if (!meta) return;
    setCategory(initialCategory);
    toast.success(`Filtrando por ${meta.label}`);
    onInitialCategoryConsumed?.();
  }, [initialCategory, byKey, onInitialCategoryConsumed]);

  const refresh = () => setReloadFlag((n) => n + 1);

  const setTxCategory = async (tx, newCat) => {
    if (newCat === tx.category) return;
    setItems((prev) => prev.map((t) => (t.transaction_id === tx.transaction_id ? { ...t, category: newCat } : t)));
    try {
      await api.put(`/finance/transactions/${tx.transaction_id}`, { category: newCat });
      const label = byKey[newCat]?.label || newCat;
      toast.success(`Categoría → ${label} · Regla aprendida`);
      // Look for other transactions that likely belong to the same recurring
      // merchant/pattern; only open the suggestion dialog if there's at least
      // one match (avoids showing an empty modal).
      try {
        const { data } = await api.post("/finance/transactions/find-similar", {
          transaction_id: tx.transaction_id,
        });
        if (data.matches && data.matches.length > 0) {
          setSimilarContext({
            txId: tx.transaction_id,
            category: newCat,
            prefetched: data,
          });
          setSimilarOpen(true);
        }
      } catch (_) { /* silent — suggestion is best-effort */ }
    } catch (err) {
      toast.error("No se pudo cambiar");
      refresh();
    }
  };

  const removeTx = async (tx) => {
    if (!window.confirm(`¿Eliminar "${tx.description}"?`)) return;
    try {
      await api.delete(`/finance/transactions/${tx.transaction_id}`);
      setItems((prev) => prev.filter((t) => t.transaction_id !== tx.transaction_id));
      setTotal((v) => v - 1);
      if (tx.status === "pending") setPendingCount((v) => Math.max(0, v - 1));
      toast.success("Eliminada");
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const approveTx = async (tx) => {
    try {
      const { data } = await api.post(`/finance/transactions/${tx.transaction_id}/approve`);
      // When we're filtering to "Pendientes" only, drop the row from view;
      // otherwise just update the row's status in-place.
      if (status === "pending") {
        setItems((prev) => prev.filter((t) => t.transaction_id !== tx.transaction_id));
      } else {
        setItems((prev) => prev.map((t) => (t.transaction_id === tx.transaction_id ? data : t)));
      }
      setPendingCount((v) => Math.max(0, v - 1));
      toast.success("Aprobada");
      onDataChanged?.();
    } catch (err) {
      toast.error("No se pudo aprobar");
    }
  };

  const toggleDeductible = async (tx) => {
    const next = !tx.is_deductible;
    // Optimistic flip
    setItems((prev) => prev.map((t) =>
      t.transaction_id === tx.transaction_id ? { ...t, is_deductible: next } : t,
    ));
    try {
      await api.put(`/finance/transactions/${tx.transaction_id}`, { is_deductible: next });
      toast.success(next ? "Marcada como deducible del taxes" : "Deducción quitada");
    } catch (err) {
      // Revert on failure
      setItems((prev) => prev.map((t) =>
        t.transaction_id === tx.transaction_id ? { ...t, is_deductible: !next } : t,
      ));
      toast.error(err.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const approveAllPending = async () => {
    if (pendingCount === 0) return;
    if (!window.confirm(`¿Aprobar todas las ${pendingCount} transacciones pendientes?`)) return;
    setApproving(true);
    try {
      const { data } = await api.post("/finance/transactions/bulk-approve", {
        scope: scope || "all",
      });
      toast.success(`${data.approved} transacción(es) aprobadas`);
      refresh();
      onDataChanged?.();
    } catch (err) {
      toast.error("No se pudo aprobar en lote");
    } finally {
      setApproving(false);
    }
  };

  const rejectAllPending = async () => {
    if (pendingCount === 0) return;
    if (!window.confirm(`¿Descartar (eliminar) TODAS las ${pendingCount} transacciones pendientes?`)) return;
    setApproving(true);
    try {
      const { data } = await api.post("/finance/transactions/bulk-reject", {
        scope: scope || "all",
      });
      toast.success(`${data.deleted} transacción(es) descartadas`);
      refresh();
      onDataChanged?.();
    } catch (err) {
      toast.error("No se pudo descartar");
    } finally {
      setApproving(false);
    }
  };

  const detectRecurring = async () => {
    setDetecting(true);
    try {
      const { data } = await api.post(`/finance/transactions/detect-recurring?scope=${scope || "all"}`);
      setCandidates((data.candidates || []).map((c) => ({ ...c, _selected: true })));
      setRecurringOpen(true);
      if (!data.candidates?.length) toast.info("No encontré patrones recurrentes.");
    } catch (err) {
      toast.error("No se pudo detectar recurrentes");
    } finally {
      setDetecting(false);
    }
  };

  const handleCategoriesChanged = () => {
    broadcastCategoriesChanged();
    refreshCategories();
    refresh();
  };

  // ----- Bulk selection -----
  const toggleSelect = (txId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(txId)) next.delete(txId);
      else next.add(txId);
      return next;
    });
  };

  const allVisibleIds = useMemo(() => items.map((t) => t.transaction_id), [items]);
  const allSelected = selected.size > 0 && allVisibleIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allVisibleIds));
    }
  };

  const clearSelection = () => setSelected(new Set());

  const bulkApproveSelected = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/finance/transactions/bulk-approve", {
        transaction_ids: Array.from(selected),
      });
      toast.success(`${data.approved} aprobada(s)`);
      clearSelection();
      refresh();
      onDataChanged?.();
    } catch (err) {
      toast.error("No se pudieron aprobar");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selected.size} transacción(es) seleccionadas?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/finance/transactions/bulk-delete", {
        transaction_ids: Array.from(selected),
      });
      toast.success(`${data.deleted} eliminada(s)`);
      clearSelection();
      refresh();
      onDataChanged?.();
    } catch (err) {
      toast.error("No se pudieron eliminar");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkChangeCategory = async () => {
    if (selected.size === 0 || !bulkCategory) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/finance/transactions/bulk-update", {
        transaction_ids: Array.from(selected),
        category: bulkCategory,
      });
      const label = byKey[bulkCategory]?.label || bulkCategory;
      toast.success(`${data.updated} recategorizadas → ${label}`);
      setBulkCategory("");
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo recategorizar");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <section
      data-testid="transactions-panel"
      className="rounded-lg border border-white/8 bg-black/20 p-4 md:p-5"
    >
      {/* Header + actions */}
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
            LEDGER · {total.toLocaleString()} MOVIMIENTOS
            {pendingCount > 0 && (
              <span
                data-testid="pending-badge"
                className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-yellow-400/20 text-yellow-300"
              >
                <Warning size={9} weight="fill" /> {pendingCount} PENDIENTE(S)
              </span>
            )}
          </div>
          <h3 className="text-base md:text-lg font-heading font-bold text-white">
            Transacciones
          </h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {pendingCount > 0 && (
            <>
              <button
                data-testid="approve-all-btn"
                onClick={approveAllPending}
                disabled={approving}
                className="on-solid flex items-center gap-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-heading font-bold px-3 py-2 text-xs transition"
                title="Aprobar todas las pendientes"
              >
                {approving ? (
                  <ArrowClockwise size={12} weight="bold" className="animate-spin" />
                ) : (
                  <CheckCircle size={12} weight="fill" />
                )}
                Aprobar todas ({pendingCount})
              </button>
              <button
                data-testid="reject-all-btn"
                onClick={rejectAllPending}
                disabled={approving}
                className="on-solid flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-heading font-bold px-3 py-2 text-xs transition"
                title="Descartar todas las pendientes"
              >
                <X size={12} weight="bold" />
                Descartar
              </button>
            </>
          )}
          <button
            data-testid="open-category-manager"
            onClick={() => setCatManagerOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-white/5 hover:bg-white/12 border border-white/10 text-white font-heading font-bold px-3 py-2 text-xs transition"
            title="Gestionar categorías"
          >
            <Tag size={12} weight="bold" />
            Categorías
          </button>
          <button
            data-testid="open-reconcile-dialog"
            onClick={() => setReconcileOpen(true)}
            disabled={items.length === 0}
            className="on-solid flex items-center gap-2 rounded-lg bg-blue-700 hover:bg-blue-800 disabled:opacity-40 text-white font-heading font-bold px-3 py-2 text-xs transition"
            title="Detectar duplicados y transferencias"
          >
            <ArrowsHorizontal size={12} weight="bold" />
            Reconciliar
          </button>
          <button
            data-testid="detect-recurring-btn"
            onClick={detectRecurring}
            disabled={detecting || items.length === 0}
            className="flex items-center gap-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-600 dark:text-emerald-300 disabled:opacity-40 font-heading font-bold px-3 py-2 text-xs transition"
          >
            {detecting ? (
              <ArrowClockwise size={12} weight="bold" className="animate-spin" />
            ) : (
              <Repeat size={12} weight="bold" />
            )}
            Detectar recurrentes
          </button>
        </div>
      </div>

      {/* Filters row: status + date range */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-black/40 border border-white/8">
          {STATUS_OPTS.map((o) => {
            const active = status === o.key;
            return (
              <button
                key={o.key}
                data-testid={`tx-status-${o.key}`}
                onClick={() => setStatus(o.key)}
                className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest font-bold rounded transition ${
                  active
                    ? o.key === "pending"
                      ? "on-solid bg-yellow-800 text-white"
                      : o.key === "approved"
                      ? "on-solid bg-emerald-700 text-white"
                      : "on-solid bg-slate-700 text-white"
                    : "text-white/45 hover:text-white/80"
                }`}
              >
                {o.label}
                {o.key === "pending" && pendingCount > 0 && (
                  <span className="ml-1 opacity-90">·{pendingCount}</span>
                )}
              </button>
            );
          })}
        </div>
        <DateRangeChips value={range} onChange={setRange} testId="tx-range" />
      </div>

      {/* Search + filter */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 focus-within:border-[#FACC15]/50 px-3 py-2">
          <MagnifyingGlass size={14} className="text-white/40" weight="bold" />
          <input
            data-testid="tx-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por descripción o comercio..."
            className="flex-1 bg-transparent outline-none text-sm text-white font-mono placeholder-white/30"
          />
          {q && (
            <button onClick={() => setQ("")} className="text-white/40 hover:text-white">
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
        <select
          data-testid="tx-category-filter"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono outline-none"
        >
          <option value="all" className="bg-[#0F1017]">Todas categorías</option>
          {categories.map((c) => (
            <option key={c.category_id} value={c.key} className="bg-[#0F1017]">
              {c.label}
            </option>
          ))}
        </select>
        <button
          data-testid="tx-deductible-filter"
          onClick={() => setDeductibleOnly((v) => !v)}
          title="Mostrar solo transacciones marcadas como deducibles del taxes"
          className={
            "rounded-lg px-3 py-2 text-[11px] font-mono font-bold uppercase tracking-widest transition border " +
            (deductibleOnly
              ? "bg-amber-500/25 text-amber-800 dark:text-amber-200 border-amber-500/60"
              : "bg-black/40 text-slate-700 dark:text-white/60 hover:text-amber-700 dark:hover:text-white border-white/10 hover:border-white/25")
          }
        >
          {deductibleOnly ? "Solo deducibles ✓" : "Deducibles"}
        </button>
      </div>

      {/* Bulk action bar (visible when any row is selected) */}
      {selected.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="rounded-lg bg-[#FACC15]/12 border border-[#FACC15]/40 px-3 py-2 mb-3 flex items-center gap-3 flex-wrap"
        >
          <div className="text-xs font-mono font-bold text-[#FACC15] uppercase tracking-widest">
            {selected.size} seleccionada(s)
          </div>
          <div className="h-4 w-px bg-white/15" />
          <div className="flex items-center gap-1.5">
            <select
              data-testid="bulk-category-select"
              value={bulkCategory}
              onChange={(e) => setBulkCategory(e.target.value)}
              className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white font-mono outline-none"
            >
              <option value="" className="bg-[#0F1017]">Recategorizar a...</option>
              {categories.map((c) => (
                <option key={c.category_id} value={c.key} className="bg-[#0F1017]">
                  {c.label}
                </option>
              ))}
            </select>
            <button
              data-testid="bulk-apply-category"
              onClick={bulkChangeCategory}
              disabled={!bulkCategory || bulkBusy}
              className="px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white text-xs font-heading font-bold flex items-center gap-1"
            >
              <Tag size={11} weight="bold" />
              Aplicar
            </button>
          </div>
          <div className="h-4 w-px bg-white/15" />
          <button
            data-testid="bulk-approve"
            onClick={bulkApproveSelected}
            disabled={bulkBusy}
            className="px-2.5 py-1.5 rounded bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white text-xs font-heading font-bold flex items-center gap-1"
          >
            <CheckCircle size={11} weight="fill" />
            Aprobar
          </button>
          <button
            data-testid="bulk-delete"
            onClick={bulkDeleteSelected}
            disabled={bulkBusy}
            className="on-solid px-2.5 py-1.5 rounded bg-red-700 hover:bg-red-800 disabled:opacity-40 text-white text-xs font-heading font-bold flex items-center gap-1"
          >
            <Trash size={11} weight="bold" />
            Eliminar
          </button>
          <div className="flex-1" />
          <button
            data-testid="bulk-clear"
            onClick={clearSelection}
            className="px-2.5 py-1.5 rounded text-white/50 hover:text-white text-xs font-mono uppercase tracking-widest"
          >
            Deseleccionar
          </button>
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div className="py-10 text-center text-xs font-mono text-white/40">
          {loading
            ? "Cargando..."
            : "Aún no hay transacciones. Sube un estado bancario en Ingresos & Gastos."}
        </div>
      ) : (
        <div className="rounded-lg border border-white/6">
          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/40 bg-black/40 border-b border-white/6 items-center rounded-t-lg relative">
            <div className="col-span-1 flex items-center">
              <button
                data-testid="select-all-toggle"
                onClick={toggleSelectAll}
                title={allSelected ? "Deseleccionar todo" : "Seleccionar todo"}
                className={`w-5 h-5 rounded flex items-center justify-center transition ${
                  allSelected
                    ? "bg-[#FACC15] text-black"
                    : someSelected
                    ? "bg-[#FACC15]/40 text-white"
                    : "bg-white/8 hover:bg-white/15 text-white/40"
                }`}
              >
                {allSelected ? (
                  <Check size={11} weight="bold" />
                ) : someSelected ? (
                  <div className="w-2.5 h-0.5 bg-white" />
                ) : (
                  ""
                )}
              </button>
            </div>
            <div className="col-span-2 relative flex items-center gap-1">
              <span>Fecha</span>
              <ColumnFilterTrigger
                testId="col-filter-date"
                active={colFilters.date.selected.size > 0 || !!colFilters.date.sortDir}
                onClick={() => setOpenFilter(openFilter === "date" ? null : "date")}
              />
              {openFilter === "date" && (
                <ColumnFilter
                  column="date"
                  values={uniqueValuesFor("date")}
                  selected={colFilters.date.selected}
                  sortDir={colFilters.date.sortDir}
                  onClose={() => setOpenFilter(null)}
                  onChange={(v) => setColFilters((p) => ({ ...p, date: v }))}
                />
              )}
            </div>
            <div className="col-span-4 relative flex items-center gap-1">
              <span>Descripción</span>
              <ColumnFilterTrigger
                testId="col-filter-description"
                active={colFilters.description.selected.size > 0 || !!colFilters.description.sortDir}
                onClick={() => setOpenFilter(openFilter === "description" ? null : "description")}
              />
              {openFilter === "description" && (
                <ColumnFilter
                  column="description"
                  values={uniqueValuesFor("description")}
                  selected={colFilters.description.selected}
                  sortDir={colFilters.description.sortDir}
                  onClose={() => setOpenFilter(null)}
                  onChange={(v) => setColFilters((p) => ({ ...p, description: v }))}
                />
              )}
            </div>
            <div className="col-span-3 relative flex items-center gap-1">
              <span>Categoría</span>
              <ColumnFilterTrigger
                testId="col-filter-category"
                active={colFilters.category.selected.size > 0 || !!colFilters.category.sortDir}
                onClick={() => setOpenFilter(openFilter === "category" ? null : "category")}
              />
              {openFilter === "category" && (
                <ColumnFilter
                  column="category"
                  values={uniqueValuesFor("category")}
                  selected={colFilters.category.selected}
                  sortDir={colFilters.category.sortDir}
                  onClose={() => setOpenFilter(null)}
                  onChange={(v) => setColFilters((p) => ({ ...p, category: v }))}
                />
              )}
            </div>
            <div className="col-span-2 relative flex items-center justify-end gap-1">
              <span>Monto</span>
              <ColumnFilterTrigger
                testId="col-filter-amount"
                active={!!colFilters.amount.sortDir}
                onClick={() => setOpenFilter(openFilter === "amount" ? null : "amount")}
              />
              {openFilter === "amount" && (
                <ColumnFilter
                  column="amount"
                  align="right"
                  values={[]}
                  selected={colFilters.amount.selected}
                  sortDir={colFilters.amount.sortDir}
                  onClose={() => setOpenFilter(null)}
                  onChange={(v) => setColFilters((p) => ({ ...p, amount: v }))}
                />
              )}
            </div>
          </div>
          <div className="max-h-[62vh] overflow-y-auto divide-y divide-white/6">
            {visibleItems.map((tx) => {
              const catMeta = byKey[tx.category];
              const accent = catMeta?.color || "#94A3B8";
              const isPending = tx.status === "pending";
              const isSelected = selected.has(tx.transaction_id);
              const rowClasses = isSelected
                ? "bg-[#FACC15]/8 border-l-2 border-[#FACC15]"
                : isPending
                ? "bg-yellow-400/8 border-l-2 border-yellow-400"
                : "border-l-2 border-emerald-500/40 hover:bg-white/4";
              return (
                <div
                  key={tx.transaction_id}
                  data-testid={`tx-row-${tx.transaction_id}`}
                  data-status={tx.status || "approved"}
                  data-selected={isSelected ? "true" : "false"}
                  className={`grid grid-cols-12 gap-2 px-3 py-2.5 group items-center ${rowClasses}`}
                >
                  <div className="col-span-1 flex items-center">
                    <button
                      data-testid={`tx-select-${tx.transaction_id}`}
                      onClick={() => toggleSelect(tx.transaction_id)}
                      className={`w-5 h-5 rounded flex items-center justify-center transition ${
                        isSelected
                          ? "bg-[#FACC15] text-black"
                          : "bg-white/8 hover:bg-white/15 text-white/40"
                      }`}
                    >
                      {isSelected && <Check size={11} weight="bold" />}
                    </button>
                  </div>
                  <div className="col-span-2 text-[11px] font-mono text-white/50 tabular-nums flex items-center gap-1.5">
                    {tx.date}
                    {isPending && (
                      <span
                        data-testid={`tx-pending-pill-${tx.transaction_id}`}
                        className="text-[8px] font-mono uppercase tracking-widest px-1 py-0.5 rounded bg-yellow-400/25 text-yellow-300 font-bold"
                        title="Pendiente de aprobación"
                      >
                        NUEVO
                      </span>
                    )}
                  </div>
                  <div className="col-span-4 flex items-center gap-2 min-w-0">
                    <div className="min-w-0">
                      <div className="text-sm font-heading font-semibold text-white truncate">
                        {tx.description}
                      </div>
                      {tx.merchant && tx.merchant !== tx.description && (
                        <div className="text-[10px] font-mono text-white/40 truncate">
                          {tx.merchant}
                        </div>
                      )}
                    </div>
                    {tx.is_recurring && (
                      <span
                        title="Recurrente"
                        className="text-[8px] font-mono uppercase tracking-widest px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-400"
                      >
                        REC
                      </span>
                    )}
                    <button
                      data-testid={`tx-deductible-${tx.transaction_id}`}
                      onClick={() => toggleDeductible(tx)}
                      title={tx.is_deductible ? "Deducible del taxes · click para quitar" : "Marcar como deducible del taxes"}
                      className={
                        "text-[8px] font-mono uppercase tracking-widest px-1 py-0.5 rounded font-bold transition " +
                        (tx.is_deductible
                          ? "bg-amber-500/25 text-amber-800 dark:text-amber-200 border border-amber-500/50"
                          : "bg-white/5 text-slate-500 dark:text-white/30 border border-transparent hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-300")
                      }
                    >
                      {tx.is_deductible ? "DEDUCE" : "$$$"}
                    </button>
                  </div>
                  <div className="col-span-3">
                    <div className="relative inline-flex items-center gap-1.5">
                      <div
                        className="w-6 h-6 rounded flex items-center justify-center shrink-0"
                        style={{ backgroundColor: accent }}
                      >
                        <CategoryIcon
                          name={catMeta?.icon || "Tag"}
                          size={11}
                          className="text-white"
                        />
                      </div>
                      <div className="relative">
                        <select
                          data-testid={`tx-cat-${tx.transaction_id}`}
                          value={tx.category}
                          onChange={(e) => setTxCategory(tx, e.target.value)}
                          className="appearance-none bg-black/40 border border-white/8 hover:border-white/20 rounded px-2 py-1 pr-6 text-[11px] font-mono text-white cursor-pointer outline-none"
                          style={{ borderLeftWidth: 3, borderLeftColor: accent }}
                        >
                          {categories.map((c) => (
                            <option key={c.category_id} value={c.key} className="bg-[#0F1017]">
                              {c.label}
                            </option>
                          ))}
                          {!byKey[tx.category] && tx.category && (
                            <option value={tx.category} className="bg-[#0F1017]">
                              {tx.category} (?)
                            </option>
                          )}
                        </select>
                        <CaretDown size={9} weight="bold" className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1.5">
                    <div
                      className={`font-mono font-bold tabular-nums text-sm ${
                        tx.amount >= 0 ? "text-emerald-400" : "text-white"
                      }`}
                    >
                      {tx.amount >= 0 ? "+" : ""}{fmtMoney(tx.amount)}
                    </div>
                    {isPending ? (
                      <button
                        data-testid={`tx-approve-${tx.transaction_id}`}
                        onClick={() => approveTx(tx)}
                        className="w-7 h-7 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-300 hover:text-emerald-200 flex items-center justify-center transition"
                        title="Aprobar"
                      >
                        <Check size={13} weight="bold" />
                      </button>
                    ) : (
                      <div className="w-7 h-7" />
                    )}
                    <button
                      onClick={() => removeTx(tx)}
                      className="w-6 h-6 rounded hover:bg-red-500/15 text-white/20 hover:text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      title="Eliminar"
                    >
                      <Trash size={11} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {recurringOpen && (
        <RecurringDialog
          candidates={candidates}
          setCandidates={setCandidates}
          onClose={() => setRecurringOpen(false)}
          onSaved={() => {
            setRecurringOpen(false);
            refresh();
            onDataChanged?.();
          }}
          scope={scope === "business" ? "business" : "personal"}
        />
      )}

      <CategoryManagerDialog
        open={catManagerOpen}
        onClose={() => setCatManagerOpen(false)}
        onChange={handleCategoriesChanged}
      />

      <ReconciliationDialog
        open={reconcileOpen}
        onClose={() => setReconcileOpen(false)}
        scope={scope}
        onApplied={() => { refresh(); onDataChanged?.(); }}
      />

      <SimilarSuggestionDialog
        open={similarOpen}
        transactionId={similarContext.txId}
        prefetched={similarContext.prefetched}
        category={similarContext.category}
        categoryLabel={byKey[similarContext.category]?.label || similarContext.category}
        categoryColor={byKey[similarContext.category]?.color || "#94A3B8"}
        categoryIcon={byKey[similarContext.category]?.icon || "Tag"}
        categoryLookup={byKey}
        onClose={() => setSimilarOpen(false)}
        onApplied={() => { refresh(); onDataChanged?.(); }}
      />
    </section>
  );
}

// ------------------------------------------------------------------
// Recurring detection dialog
// ------------------------------------------------------------------
function RecurringDialog({ candidates, setCandidates, onClose, onSaved, scope }) {
  const [saving, setSaving] = useState(false);
  const toggle = (idx) =>
    setCandidates((prev) => prev.map((c, i) => (i === idx ? { ...c, _selected: !c._selected } : c)));

  const selected = candidates.filter((c) => c._selected);

  const handlePromote = async () => {
    if (selected.length === 0) {
      toast.error("Selecciona al menos uno");
      return;
    }
    setSaving(true);
    try {
      for (const c of selected) {
        await api.post("/finance/transactions/promote-recurring", {
          merchant: c.merchant,
          amount: c.amount,
          frequency: c.frequency,
          category: c.category,
          kind: c.kind,
          scope,
          transaction_ids: c.transaction_ids,
        });
      }
      toast.success(`${selected.length} recurrente(s) agregado(s)`);
      onSaved?.();
    } catch (err) {
      toast.error("No se pudo promover");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="recurring-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col"
      >
        <div className="p-5 border-b border-white/8 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400 flex items-center gap-1.5">
              <Repeat size={12} weight="bold" /> RECURRENTES DETECTADOS
            </div>
            <div className="text-lg font-heading font-bold text-white mt-0.5">
              {candidates.length} patrón(es) encontrado(s)
            </div>
            <div className="text-[11px] font-mono text-white/50 mt-1">
              Se agregarán como Ingreso o Gasto fijo en tu Command Center.
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/50 hover:text-white flex items-center justify-center"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {candidates.length === 0 && (
            <div className="text-center py-12 text-xs font-mono text-white/30">
              No se detectaron patrones recurrentes con la información actual.
            </div>
          )}
          {candidates.map((c, i) => (
            <label
              key={i}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                c._selected
                  ? c.kind === "income"
                    ? "border-emerald-500/40 bg-emerald-500/5"
                    : "border-red-500/40 bg-red-500/5"
                  : "border-white/8 bg-black/20 opacity-60"
              }`}
            >
              <input
                type="checkbox"
                checked={c._selected}
                onChange={() => toggle(i)}
                className={`w-4 h-4 ${c.kind === "income" ? "accent-emerald-500" : "accent-red-500"}`}
                data-testid={`rec-check-${i}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-heading font-bold text-white truncate">
                    {c.merchant}
                  </div>
                  <span
                    className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${
                      c.kind === "income" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
                    }`}
                  >
                    {c.kind === "income" ? "INGRESO" : "GASTO"}
                  </span>
                  <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/8 text-white/60">
                    {c.frequency.toUpperCase()}
                  </span>
                </div>
                <div className="text-[11px] font-mono text-white/50 mt-0.5">
                  {c.occurrences} ocurrencias · {c.first_seen} → {c.last_seen}
                </div>
              </div>
              <div className={`shrink-0 text-right font-mono font-bold tabular-nums ${c.kind === "income" ? "text-emerald-400" : "text-white"}`}>
                {c.amount >= 0 ? "+" : ""}{fmtMoney(Math.abs(c.amount))}
                <div className="text-[10px] font-mono text-white/40 uppercase tracking-wider">
                  {c.frequency}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="p-4 border-t border-white/8 flex items-center justify-between gap-3">
          <div className="text-[11px] font-mono text-white/50">
            <span className="text-white font-bold tabular-nums">{selected.length}</span> seleccionado(s)
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-white/60 hover:text-white text-sm"
            >
              Cancelar
            </button>
            <button
              data-testid="promote-recurring-btn"
              onClick={handlePromote}
              disabled={saving || selected.length === 0}
              className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-heading font-bold text-sm flex items-center gap-2"
            >
              {saving ? (
                <ArrowClockwise size={13} weight="bold" className="animate-spin" />
              ) : (
                <Repeat size={13} weight="bold" />
              )}
              {saving ? "Guardando..." : `Agregar ${selected.length}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
